// The library, as a page: the producer's clips and MIDI in their folders,
// and the clips of every seed they have touched — browsed, heard, filed and
// tagged without a project open. Live's three panes, scaled to a web page:
// a sidebar of PLACES on the left (Mine and its folders, Tags, the smart
// folders, From seeds), the list in the middle (the room's material shelf in
// its page layout, so a row behaves the same here as in a room), and the
// preview's knobs below.
//
// Nothing here places a clip anywhere: that stays in a room. Upload (or
// files dropped anywhere on the page) opens the one upload flow in a modal —
// a seed, or clips for the folder on screen; a seed's clip is KEPT into that
// folder with one tap. On a phone the places are one menu above the list.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useSearchParams } from "react-router";
import { Bookmark, BookmarkPlus, ChevronDown, Disc3, Folder, FolderPlus, Keyboard, Lock, SlidersHorizontal, Sparkles, Tags, Upload, X } from "lucide-react";

import type { KeepClipResponse, MyClip, MyFolder, MySmartFolder, ReferenceClip, ReferenceMidi, ReferenceSeed } from "@shared/api-types.js";
import { usePlayback } from "@web-shared/hooks/usePlayback";
import { usePref } from "@web-shared/hooks/usePref";
import { track } from "@web-shared/lib/analytics";
import { ApiError } from "@web-shared/lib/api";
import type { ClipLibrary } from "@web-shared/lib/clip-library";
import { childFolders, folderPath } from "@web-shared/lib/material-item";
import { cn } from "@web-shared/lib/utils";
import { json, webApi } from "@web-shared/lib/web-api";
import { MATERIAL_SEARCH_ID, MaterialLibrary, type LibraryWhere, type MaterialLibraryHandle } from "@/components/MaterialLibrary";
import { AmbientGlow } from "@/components/AmbientGlow";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { intakeFromDataTransfer, type Intake } from "@/lib/web-upload/intake";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@web-shared/components/ui/dialog";
import { download, extOfKey, keyUrl, recordDownload, safeFilename, uniquePaths } from "@/lib/download";
import { ReferenceClips, referenceMidiId } from "../components/ReferenceClips";
import { useMineLibrary } from "../hooks/useMineLibrary";
import { ClipSenderContext } from "../hooks/useClipSender";
import { formatBytes } from "../hooks/useMyClips";
import { UploadFlow } from "./UploadFilesPage";

/** The page has no project: an empty one keeps the shelf's Project place honest. */
const NO_PROJECT: ClipLibrary = { tracks: [], distinctClips: 0, totalPlacements: 0, unplayableTracks: [] };

/** What Recent and the room-local stars are remembered under here. */
const PAGE_ID = "library";

type Pane = "mine" | "seeds";

const isTempo = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 20 && v <= 999;

export function LibraryPage() {
  useDocumentTitle("Library");
  const { mine, library, setDropProblem, keepMidi, sender } = useMineLibrary();
  const [pane, setPane] = useState<Pane>("mine");
  const [where, setWhere] = useState<LibraryWhere>({ folderId: null, smartId: null, tagGroup: undefined, review: false, query: "", chips: [] });
  const [help, setHelp] = useState(false);
  // The inspector column (wide windows only): the shelf draws the selection's
  // editor into it, so the thing to do with a clip is never hidden under the list.
  const [inspectorEl, setInspectorEl] = useState<HTMLDivElement | null>(null);
  const shelf = useRef<MaterialLibraryHandle>(null);
  const [fileOver, setFileOver] = useState(false);
  // The upload modal: the same flow as /upload/files, opened here. A drop
  // on the page opens it with the files already in hand.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadIntake, setUploadIntake] = useState<Promise<Intake> | null>(null);
  const [uploadWorking, setUploadWorking] = useState(false);
  const openUpload = (intake: Promise<Intake> | null): void => {
    setUploadIntake(intake);
    setUploadOpen(true);
  };

  // One engine for the page: previews only, in time with nothing but
  // themselves, at a tempo the producer sets (a loop's bars are read at it).
  const [tempo, setTempo] = usePref<number>("library:tempo", 120, isTempo);
  const playback = usePlayback([], 0, [], { arrangement: null, bpm: tempo });
  const { player } = playback;
  const { setTempo: applyTempo, stopAudition } = player;
  useEffect(() => applyTempo(tempo), [applyTempo, tempo]);
  // Leaving the page silences it; so does switching panes.
  useEffect(() => () => stopAudition(), [stopAudition]);
  useEffect(() => stopAudition(), [pane, stopAudition]);
  const [raw, setRaw] = useState(false);
  const [playOn, setPlayOn] = usePref<boolean>("material:playOn", false, (v): v is boolean => typeof v === "boolean");

  // Keeping a seed's clip: into the folder on screen, then it is a Mine row
  // like any other. What is already there answers at once.
  const [keeping, setKeeping] = useState<string | null>(null);
  const [keptNote, setKeptNote] = useState<{ text: string; error: boolean } | null>(null);
  const inMine = useMemo(() => new Set(mine.clips.map((c) => c.audioKey)), [mine.clips]);
  const folderName = useMemo(() => new Map(mine.folders.map((f) => [f.id, f.name])), [mine.folders]);
  const intoName = where.folderId ? (folderName.get(where.folderId) ?? "Mine") : "Mine";
  const keep = useCallback(
    async (seed: ReferenceSeed, clip: ReferenceClip) => {
      setKeeping(clip.audioKey);
      setKeptNote(null);
      try {
        const res = await webApi<KeepClipResponse>("/library/clips/keep", json("POST", { seedId: seed.id, audioKey: clip.audioKey, folderId: where.folderId }));
        if (!res.already) mine.added([res.clip], res.usedBytes);
        setKeptNote({ text: res.already ? `"${res.clip.name}" was already in your library.` : `Kept "${res.clip.name}" in ${intoName}.`, error: false });
        track("library_clip_kept", { detail: res.already ? "already" : "new", value: 1 });
      } catch (err) {
        setKeptNote({ text: err instanceof ApiError ? err.message : "That clip couldn't be kept. Try again in a minute.", error: true });
      } finally {
        setKeeping(null);
      }
    },
    [where.folderId, mine, intoName],
  );
  // Downloads: one clip as it is stored; a smart folder's clips as one zip.
  const downloadOne = useCallback((clip: MyClip) => {
    const name = safeFilename(clip.name.replace(/\.[a-z0-9]{2,5}$/i, ""));
    void download(name, [{ path: `${name}${extOfKey(clip.audioKey)}`, url: keyUrl(clip.audioKey) }])
      .then(() => recordDownload("clip_download", "mine|source"))
      .catch((err: unknown) => setKeptNote({ text: err instanceof Error ? err.message : "The download failed.", error: true }));
  }, []);
  const downloadMany = useCallback((clips: MyClip[], name: string) => {
    const root = safeFilename(name);
    const files = uniquePaths(clips.map((c) => ({ path: `${root}/${safeFilename(c.name.replace(/\.[a-z0-9]{2,5}$/i, ""))}${extOfKey(c.audioKey)}`, url: keyUrl(c.audioKey) })));
    setKeptNote({ text: `Packing ${clips.length} clip${clips.length === 1 ? "" : "s"}…`, error: false });
    void download(root, files)
      .then(() => {
        recordDownload("clip_download", "mine|smart");
        setKeptNote({ text: `Downloaded ${root}.zip.`, error: false });
      })
      .catch((err: unknown) => setKeptNote({ text: err instanceof Error ? err.message : "The download failed.", error: true }));
  }, []);

  // A seed's MIDI clip, kept as an idea: notes and the seed's tempo, into the folder on screen.
  const [keepingMidi, setKeepingMidi] = useState<string | null>(null);
  const [keptMidi, setKeptMidi] = useState<ReadonlySet<string>>(() => new Set());
  const keepMidiRow = useCallback(
    async (seed: ReferenceSeed, midi: ReferenceMidi) => {
      const id = referenceMidiId(seed.id, midi.key);
      setKeepingMidi(id);
      setKeptNote(null);
      const made = await keepMidi(seed.id, midi.key, where.folderId);
      setKeepingMidi(null);
      if (made) {
        setKeptMidi((prev) => new Set([...prev, id]));
        setKeptNote({ text: `Kept "${made.name}" in ${intoName} as a MIDI idea.`, error: false });
      } else setKeptNote({ text: "That MIDI clip couldn't be kept. Try again in a minute.", error: true });
    },
    [keepMidi, where.folderId, intoName],
  );
  useEffect(() => {
    if (!keptNote) return;
    const t = window.setTimeout(() => setKeptNote(null), 6000);
    return () => window.clearTimeout(t);
  }, [keptNote]);

  // Files from the desktop, anywhere on the page: the upload modal, with them.
  const dropProps = {
    onDragOver: (e: DragEvent) => {
      if (uploadOpen || !e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setFileOver(true);
    },
    onDragLeave: () => setFileOver(false),
    onDrop: (e: DragEvent) => {
      if (uploadOpen || !e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setFileOver(false);
      // Read now: a DataTransfer is only readable during its event.
      openUpload(intakeFromDataTransfer(e.dataTransfer));
    },
  };

  const go = {
    root: () => { setPane("mine"); shelf.current?.openFolder(null); },
    folder: (id: string) => { setPane("mine"); shelf.current?.openFolder(id); },
    smart: (sf: MySmartFolder) => { setPane("mine"); shelf.current?.openSmart(sf); },
    tags: () => { setPane("mine"); shelf.current?.browseTags(null); },
    review: () => { setPane("mine"); shelf.current?.openReview(); },
    seeds: () => setPane("seeds"),
  };
  // The sidebar's "open" entry; a folder path lights every crumb.
  const openPath = useMemo(() => new Set(folderPath(mine.folders, where.folderId).map((f) => f.id)), [mine.folders, where.folderId]);
  const atRoot = pane === "mine" && where.folderId === null && where.smartId === null && where.tagGroup === undefined && !where.review;
  /** Rows the browser tagged and nobody has answered: the review queue's size. */
  const reviewCount = useMemo(() => [...mine.clips, ...mine.midi].filter((r) => r.tags.some((t) => !t.confirmed)).length, [mine.clips, mine.midi]);

  const signedIn = library.signedIn;

  // The URL is the place: ?folder= ?smart= ?tags= ?review=1 ?q= ?chips= and
  // ?pane=seeds. Read once the library is known (a folder id means nothing
  // before), then written back as it changes, so a link opens what was open.
  const [searchParams, setSearchParams] = useSearchParams();
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || mine.loading) return;
    restored.current = true;
    const p = searchParams;
    if (p.get("pane") === "seeds") {
      setPane("seeds");
      return;
    }
    const folder = p.get("folder");
    const smart = p.get("smart");
    const tags = p.get("tags");
    const review = p.get("review") === "1";
    if (folder && mine.folders.some((f) => f.id === folder)) shelf.current?.openFolder(folder);
    else if (smart) {
      const sf = mine.smartFolders.find((s) => s.id === smart);
      if (sf) shelf.current?.openSmart(sf);
    } else if (tags !== null) shelf.current?.browseTags(tags === "" ? null : tags);
    else if (review) shelf.current?.openReview();
    const q = p.get("q") ?? "";
    const chips = (p.get("chips") ?? "").split(",").filter(Boolean);
    if (!review && !smart && (q !== "" || chips.length > 0)) shelf.current?.setSearch(q, chips);
  }, [mine.loading, mine.folders, mine.smartFolders, searchParams]);
  useEffect(() => {
    if (!restored.current) return;
    const next = new URLSearchParams();
    if (pane === "seeds") next.set("pane", "seeds");
    else {
      if (where.folderId) next.set("folder", where.folderId);
      if (where.smartId) next.set("smart", where.smartId);
      if (where.tagGroup !== undefined) next.set("tags", where.tagGroup ?? "");
      if (where.review) next.set("review", "1");
      if (where.smartId === null) {
        if (where.query) next.set("q", where.query);
        const chips = where.chips.filter((c) => !(where.review && c === "unreviewed"));
        if (chips.length > 0) next.set("chips", chips.join(","));
      }
    }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [pane, where, searchParams, setSearchParams]);

  // Keys for the page: ? for the list of them, / to the search box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      const typing = t !== null && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (e.key === "Escape" && help) {
        setHelp(false);
        e.preventDefault();
      } else if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "e") {
        // Live's Ctrl/Cmd+E: into the Add field of the Quick Tags.
        const add = document.getElementById("library-add-tag");
        if (add) {
          add.click();
          e.preventDefault();
        }
      } else if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      else if (e.key === "?") {
        setHelp((v) => !v);
        e.preventDefault();
      } else if (e.key === "/") {
        if (pane !== "mine") go.root();
        window.setTimeout(() => document.getElementById(MATERIAL_SEARCH_ID)?.focus(), 0);
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const searching = pane === "mine" && where.smartId === null && (where.query !== "" || where.chips.some((c) => c !== "unreviewed"));
  const counts = useMemo(() => {
    const out = new Map<string, number>();
    for (const c of mine.clips) if (c.folderId) out.set(c.folderId, (out.get(c.folderId) ?? 0) + 1);
    for (const m of mine.midi) if (m.folderId) out.set(m.folderId, (out.get(m.folderId) ?? 0) + 1);
    return out;
  }, [mine.clips, mine.midi]);

  /** Where the list is, as the phone's place menu names it. */
  const placeValue =
    pane === "seeds" ? "seeds"
    : where.smartId ? `smart:${where.smartId}`
    : where.review ? "review"
    : where.tagGroup !== undefined ? "tags"
    : where.folderId ? `folder:${where.folderId}`
    : "mine";
  const pickPlace = (v: string): void => {
    if (v === "mine") go.root();
    else if (v === "seeds") go.seeds();
    else if (v === "tags") go.tags();
    else if (v === "review") go.review();
    else if (v.startsWith("folder:")) go.folder(v.slice(7));
    else if (v.startsWith("smart:")) {
      const sf = mine.smartFolders.find((x) => x.id === v.slice(6));
      if (sf) go.smart(sf);
    }
  };
  const total = mine.clips.length + mine.midi.length;

  return (
    <div className="relative space-y-4" {...dropProps}>
      <AmbientGlow />
      {fileOver && (
        <div className="pointer-events-none fixed inset-2 z-[60] flex items-end justify-center rounded-2xl border-2 border-dashed border-primary/60 bg-[color-mix(in_srgb,var(--primary)_6%,transparent)] pb-10">
          <span className="rounded-full bg-raised px-4 py-2 text-sm font-semibold shadow-lg">Drop to upload</span>
        </div>
      )}

      {/* One line: what this is, and the one thing to do here. */}
      <header className="relative z-20 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-baseline gap-3">
          <h1 className="text-2xl font-black tracking-tight sm:text-3xl">Library</h1>
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Only you see your library">
            <Lock className="size-3" /> Private
          </span>
        </div>
        <div className="flex items-center gap-2">
          <PreviewMenu
            tempo={tempo}
            onTempo={setTempo}
            raw={raw}
            onRaw={setRaw}
            playOn={playOn}
            onPlayOn={setPlayOn}
            onKeys={() => setHelp(true)}
          />
          <button
            type="button"
            onClick={() => openUpload(null)}
            disabled={!signedIn}
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-primary px-3.5 text-xs font-bold text-primary-foreground hover:bg-white disabled:opacity-50"
          >
            <Upload className="size-4" /> Upload
          </button>
        </div>
      </header>

      {/* A phone: every place in one menu. */}
      {signedIn && (
        <label className="relative z-10 block md:hidden">
          <select
            aria-label="Library place"
            value={placeValue}
            onChange={(e) => pickPlace(e.target.value)}
            className="h-10 w-full appearance-none rounded-xl border border-panelline bg-panel pl-3 pr-9 text-sm font-semibold"
          >
            <optgroup label="Mine">
              <option value="mine">All clips ({total})</option>
              {flatFolders(mine.folders).map(({ folder, depth }) => (
                <option key={folder.id} value={`folder:${folder.id}`}>
                  {" ".repeat(depth)}{folder.name} ({counts.get(folder.id) ?? 0})
                </option>
              ))}
              {total > 0 && <option value="tags">Tags</option>}
              {reviewCount > 0 && <option value="review">Needs review ({reviewCount})</option>}
            </optgroup>
            {mine.smartFolders.length > 0 && (
              <optgroup label="Smart folders">
                {mine.smartFolders.map((sf) => (
                  <option key={sf.id} value={`smart:${sf.id}`}>{sf.name}</option>
                ))}
              </optgroup>
            )}
            <option value="seeds">From seeds</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        </label>
      )}

      {/* `minmax(0,1fr)` and `min-w-0` are load-bearing: a grid child sizes to
          its widest row otherwise, and the chips row would push the page
          wider than a phone. */}
      <div className={cn("relative z-10 grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-[200px_minmax(0,1fr)]", pane === "mine" && "lg:grid-cols-[200px_minmax(0,1fr)_280px]")}>
        <aside className="hidden md:block" aria-label="Places">
          <nav className="sticky top-20 flex flex-col gap-0.5 text-xs">
            <PlaceLink icon={<Disc3 className="size-3.5" />} label="Mine" active={atRoot} onClick={go.root} count={signedIn ? total : null} />
            {signedIn && <FolderTree folders={mine.folders} parentId={null} depth={0} counts={counts} openPath={openPath} activeId={pane === "mine" ? where.folderId : null} onOpen={go.folder} />}
            {signedIn && total > 0 && (
              <PlaceLink icon={<Tags className="size-3.5" />} label="Tags" active={pane === "mine" && where.tagGroup !== undefined} onClick={go.tags} />
            )}
            {signedIn && reviewCount > 0 && (
              <PlaceLink icon={<Sparkles className="size-3.5" />} label="Needs review" active={pane === "mine" && where.review} onClick={go.review} count={reviewCount} />
            )}
            {signedIn && (mine.smartFolders.length > 0 || searching) && (
              <details open className="group mt-3">
                <summary className="flex h-6 cursor-pointer list-none items-center gap-1 px-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground">
                  <ChevronDown className="size-3 -rotate-90 transition-transform group-open:rotate-0" /> Smart folders
                </summary>
                {mine.smartFolders.map((sf) => (
                  <PlaceLink key={sf.id} icon={<Bookmark className="size-3.5" style={sf.color ? { color: sf.color } : undefined} />} label={sf.name} active={pane === "mine" && where.smartId === sf.id} onClick={() => go.smart(sf)} />
                ))}
                {searching && <PlaceLink icon={<BookmarkPlus className="size-3.5" />} label="Save this search" active={false} onClick={() => shelf.current?.saveSearch()} />}
              </details>
            )}
            <div className="mt-3 border-t border-hairline pt-3">
              <PlaceLink icon={<FolderPlus className="size-3.5" />} label="From seeds" active={pane === "seeds"} onClick={go.seeds} />
            </div>
            {signedIn && mine.quotaBytes > 0 && <UsageMeter used={mine.usedBytes} quota={mine.quotaBytes} />}
          </nav>
        </aside>

        <section
          className={cn("flex min-h-[60dvh] min-w-0 flex-col rounded-2xl border border-hairline bg-raised p-3", fileOver && "outline outline-2 outline-primary/60")}
          aria-label={pane === "mine" ? "Mine" : "From seeds"}
        >
          {pane === "mine" ? (
            <MaterialLibrary
              layout="page"
              handleRef={shelf}
              onWhere={setWhere}
              library={NO_PROJECT}
              player={player}
              filter="mine"
              onFilter={() => {}}
              mine={{ ...library, onPlace: () => {}, onPlaceMidi: () => {}, onSaveMidi: () => {}, onPlaceReference: () => {}, onDownload: downloadOne, onDownloadMany: downloadMany }}
              inspectorTarget={inspectorEl}
              tempo={tempo}
              roomId={PAGE_ID}
              raw={raw}
              playOn={playOn}
            />
          ) : (
            <ReferenceClips
              player={player}
              raw={raw}
              onKeep={(seed, clip) => void keep(seed, clip)}
              kept={inMine}
              keeping={keeping}
              intoName={intoName}
              onKeepMidi={(seed, midi) => void keepMidiRow(seed, midi)}
              keptMidi={keptMidi}
              keepingMidi={keepingMidi}
              tempo={tempo}
            />
          )}
          {keptNote && (
            <p className={cn("mt-2 flex items-center gap-1.5 text-[11px]", keptNote.error ? "text-removed" : "text-muted-foreground")} role="status">
              {keptNote.text}
            </p>
          )}
          {/* The drop problem line lives on the shelf; From seeds shows it here. */}
          {pane === "seeds" && library.adding?.error && (
            <button type="button" onClick={() => setDropProblem(null)} className="mt-2 text-left text-[11px] text-removed underline">
              {library.adding.text} (dismiss)
            </button>
          )}
        </section>
        {pane === "mine" && (
          <aside className="hidden lg:block" aria-label="Inspector">
            <div ref={setInspectorEl} className="sticky top-20" />
          </aside>
        )}
      </div>

      {help && <ShortcutsHelp onClose={() => setHelp(false)} />}

      <Dialog
        open={uploadOpen}
        onOpenChange={(open) => {
          // A seed going up lives in this modal: it closes once it's done.
          if (!open && uploadWorking) return;
          setUploadOpen(open);
          if (!open) setUploadIntake(null);
        }}
      >
        <DialogContent className="max-h-[92dvh] w-[calc(100%-1.5rem)] max-w-xl overflow-y-auto rounded-2xl p-5 sm:p-6">
          <DialogTitle className="sr-only">Upload</DialogTitle>
          <DialogDescription className="sr-only">A seed, or clips for your library{where.folderId ? ` (into ${intoName})` : ""}.</DialogDescription>
          {uploadOpen && (
            <ClipSenderContext.Provider value={{ sender, into: pane === "mine" ? where.folderId : null }}>
              <UploadFlow
                initialParentId={null}
                embedded
                dropHeading={{ title: "Upload", sub: "A whole project becomes a seed; loose loops and one-shots go to your library." }}
                initialIntake={uploadIntake}
                onWorking={setUploadWorking}
                onClose={() => {
                  setUploadOpen(false);
                  setUploadIntake(null);
                  setPane("mine");
                }}
              />
            </ClipSenderContext.Provider>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** The folder tree flattened, parents before their children, for a menu. */
function flatFolders(folders: readonly MyFolder[], parentId: string | null = null, depth = 0): Array<{ folder: MyFolder; depth: number }> {
  return childFolders(folders, parentId).flatMap((f) => [{ folder: f, depth }, ...flatFolders(folders, f.id, depth + 1)]);
}

/** Storage, said once: a thin bar and the numbers. */
function UsageMeter({ used, quota }: { used: number; quota: number }) {
  const pct = Math.min(100, Math.round((used / quota) * 100));
  return (
    <div className="mt-4 px-2" title={`${pct}% of your library's space`}>
      <div className="h-1 overflow-hidden rounded-full bg-white/10">
        <div className={cn("h-full rounded-full", pct >= 90 ? "bg-removed" : "bg-primary/70")} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground">
        {formatBytes(used)} of {formatBytes(quota)}
      </p>
    </div>
  );
}

/**
 * How previews sound, folded into one menu: the tempo a loop is read and
 * played at, Raw (dry, from the top, once), Play on (the next row follows),
 * and the keyboard list.
 */
function PreviewMenu({
  tempo,
  onTempo,
  raw,
  onRaw,
  playOn,
  onPlayOn,
  onKeys,
}: {
  tempo: number;
  onTempo: (bpm: number) => void;
  raw: boolean;
  onRaw: (raw: boolean) => void;
  playOn: boolean;
  onPlayOn: (on: boolean) => void;
  onKeys: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(String(tempo));
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setText(String(tempo)), [tempo]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent): void => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("pointerdown", away);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("pointerdown", away);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);
  const commit = (): void => {
    const v = Number(text);
    if (isTempo(v)) onTempo(v);
    else setText(String(tempo));
  };
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/15 px-2.5 text-xs font-semibold text-muted-foreground hover:bg-white/5 hover:text-foreground"
        title="Preview settings"
      >
        <SlidersHorizontal className="size-4" />
        <span className="hidden font-mono sm:inline">{tempo} BPM</span>
      </button>
      {open && (
        <div role="dialog" aria-label="Preview" className="absolute right-0 top-full z-40 mt-2 w-60 space-y-1 rounded-xl border border-hairline bg-raised p-2 text-xs shadow-xl">
          <label className="flex h-9 items-center justify-between gap-2 px-2">
            <span>Tempo</span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                inputMode="decimal"
                aria-label="Preview tempo"
                className="h-7 w-14 rounded-md border border-panelline bg-control px-1.5 text-center text-xs text-foreground outline-none focus:border-controlline"
              />
              BPM
            </span>
          </label>
          <MenuSwitch label="Raw" hint="Dry, from the top, once" on={raw} onChange={onRaw} />
          <MenuSwitch label="Play on" hint="The next row plays after" on={playOn} onChange={onPlayOn} />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onKeys();
            }}
            className="hidden h-9 w-full items-center gap-2 rounded-lg px-2 text-left hover:bg-white/5 md:flex"
          >
            <Keyboard className="size-3.5" /> Keyboard shortcuts <kbd className="ml-auto font-mono text-[10px] text-muted-foreground">?</kbd>
          </button>
        </div>
      )}
    </div>
  );
}

function MenuSwitch({ label, hint, on, onChange }: { label: string; hint: string; on: boolean; onChange: (on: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-white/5">
      <span className="min-w-0 flex-1">
        <span className="block">{label}</span>
        <span className="block text-[10px] text-muted-foreground">{hint}</span>
      </span>
      <span className={cn("relative h-4 w-7 shrink-0 rounded-full transition-colors", on ? "bg-primary" : "bg-white/15")}>
        <span className={cn("absolute top-0.5 size-3 rounded-full bg-white transition-all", on ? "left-3.5" : "left-0.5")} />
      </span>
    </button>
  );
}

/** Every key the page answers to, by where the keyboard is. */
const LIBRARY_SHORTCUTS: ReadonlyArray<{ title: string; keys: ReadonlyArray<[string, string]> }> = [
  {
    title: "Anywhere",
    keys: [
      ["/", "Into the search box"],
      ["?", "This list"],
      ["Esc", "Close this list"],
    ],
  },
  {
    title: "Search box",
    keys: [
      ["#", "Start a tag — the list completes it"],
      ["Tab", "Take the first completion"],
      ["↓", "Into the rows"],
      ["Esc", "Clear the search"],
    ],
  },
  {
    title: "Rows",
    keys: [
      ["↑ ↓", "Walk the rows"],
      ["⇧ ↑ ↓", "Grow the selection"],
      ["⌘ A", "Select every row shown"],
      ["⏎", "Preview (⇧ ⏎ in a room, where ⏎ places)"],
      ["Esc", "Clear the selection, then the search"],
      ["⌫", "Up a folder"],
      ["⌘ E", "Add a tag to the selection"],
      ["Double-click a name", "Rename it"],
      ["⌘ click · ⇧ click", "Add one · take a range"],
    ],
  },
];

function ShortcutsHelp({ onClose }: { onClose: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="max-h-[85dvh] w-full max-w-md overflow-y-auto rounded-2xl border border-hairline bg-raised p-4 text-xs shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-bold">Keyboard</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="flex size-7 items-center justify-center rounded-lg text-muted-foreground hover:bg-white/10 hover:text-foreground">
            <X className="size-3.5" />
          </button>
        </div>
        {LIBRARY_SHORTCUTS.map((g) => (
          <section key={g.title} className="mb-3">
            <h3 className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{g.title}</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              {g.keys.map(([k, what]) => (
                <Fragment key={k + what}>
                  <dt>
                    <kbd className="rounded border border-panelline bg-panel px-1.5 py-0.5 font-mono text-[10px]">{k}</kbd>
                  </dt>
                  <dd className="text-muted-foreground">{what}</dd>
                </Fragment>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}

function PlaceLink({ icon, label, active, onClick, count = null, depth = 0 }: { icon: React.ReactNode; label: string; active: boolean; onClick: () => void; count?: number | null; depth?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn("flex h-8 w-full items-center gap-2 rounded-md px-2 text-left", active ? "bg-white/10 text-foreground" : "text-muted-foreground hover:bg-white/5 hover:text-foreground")}
      style={depth > 0 ? { paddingLeft: `${8 + depth * 14}px` } : undefined}
    >
      <span className="shrink-0">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== null && <span className="shrink-0 font-mono text-[10px] opacity-60">{count}</span>}
    </button>
  );
}

function FolderTree({ folders, parentId, depth, counts, openPath, activeId, onOpen }: { folders: readonly MyFolder[]; parentId: string | null; depth: number; counts: ReadonlyMap<string, number>; openPath: ReadonlySet<string>; activeId: string | null; onOpen: (id: string) => void }) {
  const children = childFolders(folders, parentId);
  if (children.length === 0) return null;
  return (
    <>
      {children.map((f) => (
        <div key={f.id}>
          <PlaceLink
            icon={<Folder className="size-3.5" style={f.color ? { color: f.color } : undefined} />}
            label={f.name}
            active={activeId === f.id}
            onClick={() => onOpen(f.id)}
            count={counts.get(f.id) ?? 0}
            depth={depth + 1}
          />
          {/* Children show under an open folder, or one on the way to it. */}
          {(openPath.has(f.id) || activeId === f.id) && <FolderTree folders={folders} parentId={f.id} depth={depth + 1} counts={counts} openPath={openPath} activeId={activeId} onOpen={onOpen} />}
        </div>
      ))}
    </>
  );
}
